import { useUnmount } from 'react-use';
import { create } from 'zustand';

import { useCompleteFragment } from '@eluve/apollo-client';
import { HStack, Icon, legacyTextStyles } from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import {
  useAmendments,
  useAppointmentId,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { getUserName } from '@eluve/utils';

const appointmentLastInteractionDateFragment = graphql(`
  fragment AppointmentLastInteractionDate on Appointments @_unmask {
    __typename
    id
    lastInteractionDate
  }
`);

const lastUpdatedAtDoctorInteractionNotes = graphql(`
  fragment LastUpdatedDoctorInteractionNotes on AppointmentDoctorInteractions {
    __typename
    updatedAt
    noteSignedAt
    user {
      id
      __typename
      firstName
      lastName
      email
    }
  }
`);

export const pendingUnsavedChangesStore = create(() => false);

export const AppointmentLastSaved: React.FC<{
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}> = ({ onClick }) => {
  const appointmentId = useAppointmentId();
  const pendingUnsavedChanges = pendingUnsavedChangesStore();

  useUnmount(() => {
    pendingUnsavedChangesStore.setState(false);
  });

  const additionalNotes = useCompleteFragment({
    fragment: lastUpdatedAtDoctorInteractionNotes,
    key: { appointmentId },
    strict: false,
  });

  const appointmentLastInteraction = useCompleteFragment({
    fragment: appointmentLastInteractionDateFragment,
    key: { id: appointmentId },
    strict: false,
  });

  const { amendments: submittedAmendments } = useAmendments(appointmentId, {
    submittedOnly: true,
  });

  const lastSubmittedAmendment =
    submittedAmendments[submittedAmendments.length - 1];

  const getLastSavedMessage = () => {
    if (lastSubmittedAmendment?.submittedAt) {
      return `Last amended by ${getUserName(lastSubmittedAmendment.user)} at ${formatDate.toMMMMdyyyyWithTime(new Date(lastSubmittedAmendment.submittedAt))}`;
    }
    if (additionalNotes?.noteSignedAt) {
      return `Last signed by ${getUserName(additionalNotes.user)} at ${formatDate.toMMMMdyyyyWithTime(new Date(additionalNotes.noteSignedAt))}`;
    }
    if (appointmentLastInteraction?.lastInteractionDate) {
      return `Last saved at ${formatDate.toMMMMdyyyyWithTime(new Date(appointmentLastInteraction.lastInteractionDate))}`;
    }
    if (pendingUnsavedChanges) {
      return 'Saving...';
    }
    return '';
  };

  const message = getLastSavedMessage();
  if (!message) {
    return null;
  }

  return (
    <div className={onClick ? 'cursor-pointer' : ''} onClick={onClick}>
      <HStack>
        <Icon
          name={pendingUnsavedChanges ? 'Loader2Icon' : 'History'}
          size="xs"
          className={
            pendingUnsavedChanges
              ? 'animate-spin text-border-accent-quaternary'
              : ''
          }
        />
        <span
          className={`${legacyTextStyles.body({ color: 'tertiary', size: 'm' })} hidden sm:inline`}
        >
          {message}
        </span>
      </HStack>
    </div>
  );
};
